import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
// import { Link } from 'react-router-dom'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {
  Heart,
  Clock,
  ShoppingCart,
  ChefHat,
  Users,
  Star,
  CheckCircle,
  ArrowRight,
  Calendar,
  Home,
  Award,
  Target,
  Shield,
  CreditCard,
  Leaf,

  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import AIVisual from '../components/ui/AIVisual'

const MealPlanningHomepage: React.FC = () => {
  const [currentBanner, setCurrentBanner] = useState(0)

  // Elite Delivery Partners
  const elitePartnerVicinity = [
    { name: "Primal Kitchen Downtown", distance: "0.8 miles", type: "Gourmet Paleo", status: "Open" },
    { name: "Green Garden West", distance: "1.2 miles", type: "Organic Vegan", status: "Closing Soon" },
    { name: "Helix Proteins North", distance: "2.4 miles", type: "Bioavailable Animal", status: "Open" },
  ]

  // Banner content for hero section
  const bannerContent = [
    {
      title: "Weekly Meal Plans Coordinated with Your Labs",
      subtitle: "Optional meal planning for BIOTUNE members who want help turning monthly lab insights into everyday meals.",
      image: "https://images.unsplash.com/photo-1547592117-65715877f32d?auto=format&fit=crop&q=80&w=1600",
      cta: "View BIOTUNE Plans"
    },
    {
      title: "No More Grocery Shopping Stress",
      subtitle: "Get exactly what you need, when you need it. Pre-portioned ingredients eliminate food waste and save money",
      image: "https://images.unsplash.com/photo-1506484334402-40ff44e0556f?auto=format&fit=crop&q=80&w=1600",
      cta: "See How It Works"
    },
    {
      title: "Eat Healthy, Save Time, Live Better",
      subtitle: "Nutritionist-approved meal plans tailored to your dietary needs and delivered weekly",
      image: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&q=80&w=1600",
      cta: "Get Started Today"
    }
  ]

  // Trust badges
  const trustBadges = [
    { icon: Shield, text: "Fresh ingredients focus", color: "text-theme-success" },
    { icon: Clock, text: "Designed to save time", color: "text-theme-primary" },
    { icon: CreditCard, text: "Flexible add-on for members", color: "text-theme-accent" }
  ]

  // Core benefits
  const coreBenefits = [
    {
      icon: Clock,
      title: "Save Time Every Week",
      description: "Eliminate meal planning, grocery shopping, and prep time. Get your Sundays back!"
    },
    {
      icon: ShoppingCart,
      title: "No More Grocery Trips",
      description: "Fresh ingredients delivered to your door. Skip the crowded stores and long lines."
    },
    {
      icon: ChefHat,
      title: "Professional Meal Plans",
      description: "Nutritionist-designed recipes that are delicious, healthy, and easy to prepare."
    },
    {
      icon: Target,
      title: "Reduce Food Waste",
      description: "Pre-portioned ingredients mean you get exactly what you need - no more, no less."
    }
  ]

  // How it works
  const howItWorks = [
    {
      number: 1,
      title: "Choose Your Plan",
      description: "Select from family, couple, or individual meal plans with dietary preferences",
      icon: Calendar
    },
    {
      number: 2,
      title: "Get Fresh Ingredients",
      description: "Premium partners deliver pre-portioned, farm-fresh ingredients to your door",
      icon: Home
    },
    {
      number: 3,
      title: "Cook & Enjoy",
      description: "Follow simple step-by-step recipes and enjoy healthy, delicious meals in 30 minutes or less",
      icon: ChefHat
    }
  ]

  // Meal plan options
  const mealPlanOptions = [
    {
      category: "Family Plans",
      features: ["4 servings per meal", "Kid-friendly recipes", "30 min cook time", "Balanced nutrition"],
      icon: Users,
      color: "from-blue-500 to-blue-600"
    },
    {
      category: "Couple Plans",
      features: ["2 servings per meal", "Gourmet options", "20 min cook time", "Restaurant quality"],
      icon: Heart,
      color: "from-pink-500 to-pink-600"
    },
    {
      category: "Individual Plans",
      features: ["Single servings", "Quick recipes", "15 min cook time", "Portion controlled"],
      icon: Target,
      color: "from-green-500 to-green-600"
    },
    {
      category: "Specialty Plans",
      features: ["Keto, Vegan, Paleo", "Gluten-free options", "Organic ingredients", "Custom macros"],
      icon: Leaf,
      color: "from-theme-accent to-theme-accent/80"
    }
  ]

  // Partner benefits
  const partnerBenefits = [
    {
      title: "Premium Ingredients",
      description: "Farm-fresh produce, organic proteins, and artisanal products from trusted local partners",
      image: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80&w=800"
    },
    {
      title: "Time-Saving Convenience",
      description: "Pre-portioned ingredients and detailed recipe cards save you hours of prep time every week",
      image: "https://images.unsplash.com/photo-1556911220-e15595bb478a?auto=format&fit=crop&q=80&w=800"
    },
    {
      title: "Health & Nutrition",
      description: "Registered dietitians create balanced meal plans that meet your nutritional needs and health goals",
      image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=800"
    },
    {
      title: "Cost Effective",
      description: "Save money by eliminating impulse purchases and reducing food waste with perfectly portioned ingredients",
      image: "https://images.unsplash.com/photo-1543332164-6e82f35557ad?auto=format&fit=crop&q=80&w=800"
    }
  ]

  // Pricing plans
  const pricingPlans = [
    {
      name: "Starter",
      price: "$69",
      period: "per week",
      description: "Perfect for individuals or couples trying meal planning",
      features: [
        "3 meals per week",
        "2 servings each",
        "Free delivery",
        "Recipe cards included",
        "Cancel anytime"
      ],
      popular: false
    },
    {
      name: "Family",
      price: "$129",
      period: "per week",
      description: "Most popular - ideal for busy families",
      features: [
        "5 meals per week",
        "4 servings each",
        "Free delivery",
        "Kid-friendly recipes",
        "Nutritionist support",
        "Priority customer service"
      ],
      popular: true
    },
    {
      name: "Premium",
      price: "$199",
      period: "per week",
      description: "Gourmet meals with premium ingredients",
      features: [
        "7 meals per week",
        "4 servings each",
        "Free delivery",
        "Premium proteins",
        "Organic produce",
        "Wine pairing suggestions",
        "Personal chef consultation"
      ],
      popular: false
    }
  ]

  // Testimonials
  const testimonials = [
    {
      name: "Sarah Mitchell",
      location: "Busy Mom of 3",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200",
      text: "This service has been a game-changer for our family. We save at least 6 hours every week, and my kids love helping prepare the meals. The ingredients are always fresh, and we've tried foods we never would have picked ourselves!",
      rating: 5,
      timeSaved: "6+ hours/week"
    },
    {
      name: "David Chen",
      location: "Health-Conscious Professional",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200",
      text: "As someone who works 60+ hours a week, I was eating takeout almost every night. Now I have healthy, delicious meals ready in 20 minutes. I've lost 15 pounds and actually enjoy cooking again.",
      rating: 5,
      timeSaved: "8+ hours/week"
    },
    {
      name: "Maria Rodriguez",
      location: "Retired Teacher",
      image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80&w=200",
      text: "I was skeptical about the cost at first, but we actually save money now. No more impulse purchases at the grocery store, and we waste zero food. Everything gets used perfectly.",
      rating: 5,
      timeSaved: "4+ hours/week"
    }
  ]

  // Auto-rotate banner
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % bannerContent.length)
    }, 6000)
    return () => clearInterval(interval)
  }, [])

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % bannerContent.length)
  }

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + bannerContent.length) % bannerContent.length)
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-theme-surface via-theme-bg to-theme-elevated">
        {/* Hero Content */}
        <div className="relative pt-32 pb-20">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
              {/* Left Column - Content */}
              <motion.div
                key={currentBanner}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                <div className="space-y-6">
                  <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-theme-text-primary leading-tight">
                    {bannerContent[currentBanner].title}
                  </h1>
                  <p className="text-xl md:text-2xl text-theme-text-secondary leading-relaxed">
                    {bannerContent[currentBanner].subtitle}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" className="text-lg bg-theme-primary hover:bg-theme-primary/90 text-theme-text-inverse">
                    {bannerContent[currentBanner].cta}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg border-theme-primary text-theme-primary hover:bg-theme-primary/5">
                    View Sample Menu
                  </Button>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap gap-6 pt-8">
                  {trustBadges.map((badge, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-center space-x-2"
                    >
                      <badge.icon className={`w-5 h-5 ${badge.color}`} />
                      <span className="text-sm font-medium text-theme-text-primary">{badge.text}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Time Saved Counter */}
                <div className="bg-theme-surface rounded-2xl p-6 shadow-lg border border-theme-border">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-theme-primary mb-2">Time Back</div>
                    <div className="text-theme-text-secondary">Designed to reduce weekly planning overhead</div>
                    <div className="text-sm text-theme-text-muted mt-2">Actual time savings will vary by household.</div>
                  </div>
                </div>
              </motion.div>

              {/* Right Column - Visual */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <AIVisual
                    src={bannerContent[currentBanner].image}
                    alt={bannerContent[currentBanner].title}
                    fallbackType="food"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                {/* Banner Navigation */}
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex items-center space-x-4">
                  <button
                    onClick={prevBanner}
                    className="p-2 rounded-full bg-theme-surface/90 backdrop-blur-sm hover:bg-theme-surface transition-colors shadow-lg"
                  >
                    <ChevronLeft className="w-5 h-5 text-theme-primary" />
                  </button>
                  <div className="flex space-x-2">
                    {bannerContent.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentBanner(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${index === currentBanner ? 'bg-theme-accent' : 'bg-theme-border'
                          }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={nextBanner}
                    className="p-2 rounded-full bg-theme-surface/90 backdrop-blur-sm hover:bg-theme-surface transition-colors shadow-lg"
                  >
                    <ChevronRight className="w-5 h-5 text-theme-primary" />
                  </button>
                </div>
              </motion.div>
            </div>
          </Container>
        </div>
      </section>

      {/* Core Benefits Section */}
      <section id="features" className="py-20 bg-theme-bg">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-theme-text-primary mb-4">
              Transform Your Weekly Routine
            </h2>
            <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto">
              Discover how our meal planning service eliminates the stress of "What's for dinner?" while saving you time and money.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {coreBenefits.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-theme-primary to-theme-primary/80 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-theme-text-inverse" />
                </div>
                <h3 className="text-xl font-semibold text-theme-text-primary mb-3">
                  {item.title}
                </h3>
                <p className="text-theme-text-secondary">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* How It Works */}
          <div className="bg-gradient-to-r from-theme-surface to-theme-elevated rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-theme-text-primary text-center mb-12">
              How Meal Planning Works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {howItWorks.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="relative text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-theme-primary to-theme-accent rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl font-bold text-theme-text-inverse">{step.number}</span>
                  </div>
                  <div className="w-12 h-12 bg-theme-surface rounded-xl flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-6 h-6 text-theme-primary" />
                  </div>
                  <h4 className="text-lg font-semibold text-theme-text mb-2">
                    {step.title}
                  </h4>
                  <p className="text-theme-secondary">
                    {step.description}
                  </p>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-theme-border transform -translate-y-1/2" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Meal Plan Options */}
      <section className="py-20 bg-theme-surface-light">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-theme-text-primary mb-4">
              Plans for Every Lifestyle
            </h2>
            <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto">
              Whether you're feeding a family, cooking for two, or focusing on specific dietary needs, we have the perfect meal plan for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {mealPlanOptions.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-2 hover:border-theme-primary/30 bg-theme-surface">
                  <CardHeader>
                    <div className={`w-12 h-12 bg-gradient-to-r ${category.color} rounded-lg flex items-center justify-center mb-4`}>
                      <category.icon className="w-6 h-6 text-theme-text-inverse" />
                    </div>
                    <CardTitle className="text-xl text-theme-text-primary">
                      {category.category}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {category.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center space-x-2 text-theme-text-secondary">
                          <CheckCircle className="w-4 h-4 text-theme-success" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Partner Program Details */}
          <div className="bg-theme-surface rounded-2xl p-8 shadow-lg border border-theme-border">
            <h3 className="text-3xl font-bold text-theme-text-primary text-center mb-12">
              Partner Meal Delivery Program
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {partnerBenefits.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-6 bg-theme-primary/5 rounded-xl p-6 border border-theme-primary/10"
                >
                  <AIVisual
                    src={item.image}
                    alt={item.title}
                    fallbackType="food"
                    aspectRatio="square"
                    className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                  />
                  <div>
                    <h4 className="text-xl font-semibold text-theme-text-primary mb-2">
                      {item.title}
                    </h4>
                    <p className="text-theme-text-secondary">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Elite Vicinity Selector */}
            <div className="mt-16 p-8 rounded-3xl bg-gradient-to-br from-theme-primary/10 to-transparent border border-theme-primary/20 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform">
                <Award className="w-32 h-32 text-theme-primary" />
              </div>
              
              <div className="relative z-10 max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-theme-primary text-black text-[10px] font-black uppercase rounded-full mb-4">
                  <Star className="w-3 h-3 fill-current" /> Elite Member Feature
                </div>
                <h3 className="text-3xl font-bold mb-4">Hyper-Local Neighborhood Sync</h3>
                <p className="text-theme-text-secondary mb-8">
                  As an Elite member, you can choose direct delivery from partners in your immediate vicinity. Our "Secure Handshake" protocol ensures they receive your food plan, but never your health data.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {elitePartnerVicinity.map((partner, idx) => (
                    <button key={idx} className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-theme-primary transition-all text-left">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-bold">{partner.name}</span>
                        <span className="text-[10px] text-theme-primary font-mono">{partner.distance}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/40 uppercase tracking-widest">{partner.type}</span>
                        <div className="w-1 h-1 rounded-full bg-white/20" />
                        <span className="text-[10px] text-theme-success">{partner.status}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-theme-bg">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-theme-text-primary mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto">
              Choose the plan that fits your lifestyle. All plans include free delivery and access to our partner network.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <Card className={`h-full bg-theme-surface ${plan.popular ? 'border-theme-primary border-2 shadow-xl' : 'border-theme-border'}`}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-theme-primary text-theme-text-inverse px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </div>
                    </div>
                  )}
                  <CardHeader className="text-center pb-6">
                    <h3 className="text-2xl font-bold text-theme-text-primary mb-2">
                      {plan.name}
                    </h3>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-theme-text-primary">{plan.price}</span>
                      <span className="text-theme-text-secondary">/{plan.period}</span>
                    </div>
                    <p className="text-theme-text-secondary">
                      {plan.description}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center space-x-3">
                          <CheckCircle className="w-5 h-5 text-theme-success" />
                          <span className="text-theme-text-primary">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className={`w-full ${plan.popular ? 'bg-theme-primary hover:bg-theme-primary/90 text-theme-text-inverse' : ''}`}>
                      View BIOTUNE Plans
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-theme-text-secondary mb-4">
              Meal planning is available as an optional service for BIOTUNE members through select partner programs. Availability, pricing, and delivery terms vary by partner.
            </p>
          </div>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-theme-surface-light">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-theme-text-primary mb-4">
              What Early Users Are Saying
            </h2>
            <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto">
              Real stories from people who transformed their relationship with meal planning and cooking.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 bg-theme-surface border-theme-border">
                  <CardContent className="pt-6">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-theme-warning fill-current" />
                      ))}
                    </div>
                    <p className="text-theme-text-secondary mb-6 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <h4 className="font-semibold text-theme-text-primary">{testimonial.name}</h4>
                          <p className="text-sm text-theme-text-secondary">{testimonial.location}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-theme-success">{testimonial.timeSaved}</div>
                        <div className="text-xs text-theme-text-muted">Time Saved</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Success Stats */}
          <div className="bg-theme-surface rounded-2xl p-8 shadow-lg border border-theme-border">
            <div className="text-center text-theme-text-muted text-sm">
              Stories and examples are illustrative. Actual experiences will vary by household, partner, and BIOTUNE plan.
            </div>
          </div>
        </Container>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-theme-primary to-theme-primary/80">
        <Container>
          <div className="text-center text-theme-text-inverse">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Get Your Time Back?
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Join MealMate today and start enjoying stress-free meal planning with fresh ingredients delivered to your door.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button size="lg" className="text-lg bg-theme-surface text-theme-primary hover:bg-theme-surface-light border-0 shadow-xl">
                  View BIOTUNE Plans
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button size="lg" variant="outline" className="text-lg border-theme-text-inverse text-theme-text-inverse hover:bg-theme-text-inverse/10">
                  Talk to Our Team
                </Button>
              </div>

              <div className="flex flex-wrap justify-center gap-6 text-sm opacity-90">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Optional add-on service for BIOTUNE members</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Availability, pricing, and delivery terms vary by partner</span>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>


    </div>
  )
}

export default MealPlanningHomepage
